import React, { useState, useRef, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEdit, FaTrash, FaFileExport, FaSms, FaFileAlt, FaPlusCircle, FaMinusCircle } from "react-icons/fa";
import { toast } from 'react-toastify';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Server from "../../Shared/Server";
import { API_BASE_URL } from '../../Shared/Server';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const FILTER_STORAGE_KEY = 'serverSideLocations:filterModel';
const KEEP_FILTER_FLAG = 'serverSideLocations:keepFilter';

const emptyCellFormatter = (params) =>
  params.value === null || params.value === undefined || params.value === '' ? '-' : params.value;

const ServerSideLocations = () => {
  const [show, setShow] = useState(false);
  const [publicID, setPublicID] = useState();
  const [isDeleting, setIsDeleting] = useState(false);
  const gridApi = useRef();
  const gridRef = useRef();
  const navigate = useNavigate();
  const current_user = JSON.parse(localStorage.getItem('user'));

  // Make outer page-wrapper full-width for this page only
  // Clear saved filters unless we're returning from a keep-filter route
  useEffect(() => {
    if (sessionStorage.getItem(KEEP_FILTER_FLAG)) {
      sessionStorage.removeItem(KEEP_FILTER_FLAG);
    } else {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    }

    const wrapper = document.querySelector('.page-wrapper.container-xl');
    if (wrapper) {
      wrapper.style.maxWidth = '100%';
      wrapper.style.padding = '0 12px';
    }
    return () => {
      if (wrapper) {
        wrapper.style.maxWidth = '';
        wrapper.style.padding = '';
      }
    };
  }, []);

  const columnDefs = useMemo(() => [
    {
      headerName: "Actions",
      width: 242,
      pinned: 'left',
      sortable: false,
      filter: false,
      cellRenderer: (e) => {
        const { data } = e;
        if (!data) return null;
        const isActive = data.status === 'Active';
        return (
          <div className="d-flex gap-1">
            <Link
              className={`btn ${isActive ? 'btn-outline-success' : 'btn-outline-warning'}`}
              to={isActive ? "/disconnect-location" : "/reconnect-location"}
              state={{ location_id: data.location_pk }}
              title={isActive ? 'Suspend' : 'Resume'}
            >
              {isActive ? <FaMinusCircle /> : <FaPlusCircle />}
            </Link>
            <Link
              className="btn btn-outline-success"
              to="/location-billing-history"
              state={{ data }}
              title="Billing History"
              onClick={() => { sessionStorage.setItem(KEEP_FILTER_FLAG, '1'); }}
            >
              <FaFileAlt />
            </Link>
            <Link
              className="btn btn-outline-info"
              to="/location-message-history"
              state={{ data }}
              title="Message History"
              onClick={() => { sessionStorage.setItem(KEEP_FILTER_FLAG, '1'); }}
            >
              <FaSms />
            </Link>
            <Link
              className="btn btn-outline-info"
              to="/update-location"
              state={{ data }}
              title="Edit"
            >
              <FaEdit />
            </Link>
            <Link
              className="btn btn-outline-warning"
              to="/location-suspension-history"
              state={{ data }}
              title="Suspension History"
              onClick={() => { sessionStorage.setItem(KEEP_FILTER_FLAG, '1'); }}
            >
              <FaFileAlt />
            </Link>
            <button
              className="btn btn-outline-danger"
              onClick={() => handleShow(data.public_id)}
              title="Delete"
            >
              <FaTrash />
            </button>
            {data.contract_path && (
              <a
                className="btn btn-outline-orange"
                href={`${API_BASE_URL}/${data.contract_path}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Contract"
              >
                <FaFileExport />
              </a>
            )}
          </div>
        );
      },
    },
    { headerName: "ID", field: "location_id", width: 105 },
    { headerName: "Name", field: "full_name", width: 230 },
    { headerName: "Remark", field: "remark", width: 120 },
    { headerName: "Contact", field: "location_contact", width: 120 },
    { headerName: "PID", field: "previous_id", width: 105 },
    { headerName: "Serial No.", field: "serial_number", width: 130 },
    { headerName: "Lock No.", field: "lock_number", width: 120 },
    { headerName: "Category", field: "category", width: 105 },
    { headerName: "Group", field: "group_name", width: 110 },
    { headerName: "Description", field: "description", width: 110 },
    { headerName: "Customer Contact", field: "contact_number", width: 110 },
    { headerName: "Date", field: "date", width: 110 },
    { headerName: "Village", field: "village_name", width: 110 },
    { headerName: "Zone", field: "zone_name", width: 130 },
    { headerName: "Station", field: "station_name", width: 105 },
    { headerName: "Line", field: "line_name", width: 105 },
    { headerName: "Rate", field: "rate_name", width: 105 },
    { headerName: "Status", field: "status", width: 75 },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: false,
    filter: true,
    resizable: true,
    valueFormatter: emptyCellFormatter,
    filterParams: {
      buttons: []
    }
  }), []);

  const fetchData = async (params) => {
    // Only data columns (those with a `field`) — keeps mapping robust to reordering
    const dataColumns = columnDefs.filter(col => col.field);
    const requestParams = {
      draw: 1,
      branch_id: current_user?.branch?.id || 0,
      start: params.startRow,
      length: params.endRow - params.startRow,
      columns: dataColumns.map(col => ({
        data: col.field,
        searchable: col.filter !== false,
        orderable: !!col.sortable,
        search: { value: '', regex: false }
      })),
      order: params.sortModel.map(sort => ({
        column: dataColumns.findIndex(col => col.field === sort.colId),
        dir: sort.sort
      })),
      search: Object.keys(params.filterModel).map(key => ({
        col: key,
        value: params.filterModel[key].filter?.toString().toLowerCase() ?? '',
        type: params.filterModel[key].type || "contains"
      })),
      filter: {}
    };

    try {
      const response = await Server.post('/locations/serverside', requestParams);
      params.successCallback(response.data.data, response.data.recordsFiltered);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load locations');
      params.failCallback();
    }
  };

  const onGridReady = (params) => {
    gridApi.current = params.api;

    try {
      const saved = sessionStorage.getItem(FILTER_STORAGE_KEY);
      if (saved) {
        params.api.setFilterModel(JSON.parse(saved));
      }
    } catch (e) {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    }

    params.api.updateGridOptions({
      datasource: {
        getRows: (p) => fetchData(p)
      }
    });
  };

  const onFilterChanged = (params) => {
    const model = params.api.getFilterModel();
    if (model && Object.keys(model).length > 0) {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(model));
    } else {
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
    }
  };

  const onFirstDataRendered = () => {};

  const onGridSizeChanged = () => {};

  const handleClose = () => {
    if (isDeleting) return;
    setShow(false);
  };

  const handleShow = (public_id) => {
    setShow(true);
    setPublicID(public_id);
  };

  const handleDelete = async (public_id) => {
    setIsDeleting(true);
    try {
      const response = await Server.delete(`/locations/${public_id}`);
      toast.success(response.data.detail);
      gridApi.current?.purgeInfiniteCache();
      setShow(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate('/login');
      } else if (error?.response?.status === 403) {
        toast.error(error.response.data.detail);
        navigate('/');
      } else {
        toast.warning(error?.response?.data?.detail || 'Failed to delete location');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="row mb-2">
        <div className="col-md-12">
          <Link to="/multi-part" className="btn btn-outline-info float-end">
            Add Location
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
          <h3 className="card-title mb-0">Locations</h3>
        </div>
        <div className="card-body p-3">
          <div className="ag-theme-alpine" style={{ height: 'calc(100vh - 250px)', width: '100%' }}>
            <AgGridReact
              ref={gridRef}
              rowModelType="infinite"
              cacheBlockSize={25}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              rowSelection="multiple"
              enableCellTextSelection={true}
              suppressCellFocus={true}
              onGridReady={onGridReady}
              onFilterChanged={onFilterChanged}
            />
          </div>
        </div>
      </div>

      <Modal show={show} onHide={handleClose} backdrop={isDeleting ? 'static' : true}>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Delete Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this location? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isDeleting}>
            No
          </Button>
          <Button variant="danger" onClick={() => handleDelete(publicID)} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ServerSideLocations;
